import React from "react";
import { connect, ConnectedProps } from "react-redux";
import { Button, Header, Icon, Loader, Segment, Tab } from "semantic-ui-react";
import { messages } from "../../../../config/constants";
import { AppDispatch, RootState } from "../../../../reducers";
import {
  disable2fa,
  get2faStatus,
  reset2faSetupState
} from "../../../../store/twoFactorAuthentication";
import ConfirmationPrompt from "../../../common/ConfirmationPrompt";
import DismissibleMessage from "../../../common/DismissibleMesage";
import TwoFactorAuthenticationSetup from "./TwoFactorAuthenticationSetup";

const mapStateToProps = ({
  twoFactorAuthentication: {
    is2faEnabled,
    isGet2faStatusLoading,
    get2faStatusSuccess,
    isDisable2faLoading
  }
}: RootState) => ({
  is2faEnabled,
  isGet2faStatusLoading,
  get2faStatusSuccess,
  isDisable2faLoading
});
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  get2faStatus: () => dispatch(get2faStatus()),
  reset2faSetupState: () => dispatch(reset2faSetupState()),
  disable2fa: () => dispatch(disable2fa())
});
const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
type SecurityTabProps = { isExternal: boolean } & PropsFromRedux;
type SecurityTabState = {
  is2faSetupViewActive: boolean;
};

class SecurityTab extends React.Component<SecurityTabProps, SecurityTabState> {
  constructor(props: SecurityTabProps) {
    super(props);
    this.state = { is2faSetupViewActive: false };
    const { is2faEnabled, isGet2faStatusLoading, get2faStatusSuccess } = props;
    if (is2faEnabled === null && !isGet2faStatusLoading && !get2faStatusSuccess) {
      this.props.get2faStatus();
    }
  }

  twoFactorDescription =
    "Two-factor authentication adds an additional layer of security to your account by\n" +
    "            requiring more than just a password to sign in.";

  mobileRequiredDescription =
    "To enable two-factor authentication you must have access to a " +
    "smart phone with a camera with permissions to install an authentication application.";

  render2faDisabledView() {
    return (
      <>
        <Segment placeholder>
          <Header icon>
            <Icon name="lock open" />
            Two-factor authentication is not enabled yet.
          </Header>
          <p style={{ textAlign: "center" }}>
            {this.twoFactorDescription}
            <br />
            {this.mobileRequiredDescription}
          </p>
          <Button
            positive
            style={{ maxWidth: "300px" }}
            onClick={() => this.setIs2faSetupViewActive(true)}
          >
            Enable Two-factor authentication
          </Button>
        </Segment>
      </>
    );
  }

  render2faEnabledView() {
    return (
      <Segment placeholder>
        <Header icon>
          <Icon name="lock" />
          Two-factor authentication is enabled for you account.
        </Header>
        <p style={{ textAlign: "center" }}>{this.twoFactorDescription}</p>
        <ConfirmationPrompt
          trigger={
            <Button negative style={{ maxWidth: "300px" }} loading={this.props.isDisable2faLoading}>
              Disable Two-factor authentication
            </Button>
          }
          buttonLabel="Remove"
          headerIcon="lock open"
          onConfirmation={() => this.props.disable2fa()}
          content="Disable two-factor authentication?"
        />
      </Segment>
    );
  }

  setIs2faSetupViewActive(isActive: boolean) {
    this.setState({
      is2faSetupViewActive: isActive
    });
  }

  tabWrapper(value: any) {
    return (
      <Tab.Pane attached={false} className="management-tab-pane" as="segment">
        {value}
      </Tab.Pane>
    );
  }

  render() {
    const { is2faSetupViewActive } = this.state;
    if (is2faSetupViewActive) {
      return this.tabWrapper(
        <TwoFactorAuthenticationSetup
          onClose={() => {
            this.setIs2faSetupViewActive(false);
            this.props.reset2faSetupState();
          }}
          onSuccess={() => {
            this.props.get2faStatus();
            this.setIs2faSetupViewActive(false);
          }}
        />
      );
    }

    if (this.props.isGet2faStatusLoading) {
      return this.tabWrapper(<Loader />);
    }

    return this.tabWrapper(
      <>
        <Header as="h2">Two-factor authentication</Header>
        {this.props.isExternal ? (
          <DismissibleMessage
            className={messages.hasExternalSubscription.className}
            content={messages.hasExternalSubscription.content}
          />
        ) : this.props.is2faEnabled ? (
          this.render2faEnabledView()
        ) : (
          this.render2faDisabledView()
        )}
      </>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(SecurityTab);
